import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { approveDiscount, call, putSiblingRule } from '@/api/desk';
import { ResourcePanel } from '@/features/shared/ops/crud';
import { DataTable, FormModal, KV, Link2, Modal, Notice, PageHeader, Tabs, arr, classLabel, dOnly, errMsg, inputCls, money, useAction, useClasses, useGet, useSessions, useTerms } from '@/features/shared/ops/kit';

const CATEGORIES = ['tuition', 'uniform', 'books', 'transport', 'exam', 'levy', 'other'].map((c) => ({ value: c, label: c }));

function FeeHeads() {
  return (
    <ResourcePanel
      qkey="feeheads"
      url="/school/fee-heads"
      plain
      createLabel="Add fee head"
      createFields={[
        { name: 'name', label: 'Name', required: true, maxLength: 80 },
        { name: 'code', label: 'Code', maxLength: 20 },
        { name: 'category', label: 'Category', type: 'select', required: true, options: CATEGORIES },
        { name: 'is_optional', label: 'Optional (parents may skip)', type: 'checkbox' },
      ]}
      editUrl={(r) => `/school/fee-heads/${r.id}`}
      editFields={[
        { name: 'name', label: 'Name', required: true, maxLength: 80 },
        { name: 'code', label: 'Code', maxLength: 20 },
        { name: 'category', label: 'Category', type: 'select', options: CATEGORIES },
        { name: 'is_optional', label: 'Optional', type: 'checkbox' },
        { name: 'is_active', label: 'Active', type: 'checkbox' },
      ]}
      empty="No fee heads yet. Add one such as Tuition or Uniform."
      cols={[
        { head: 'Name', cell: (r) => r.name },
        { head: 'Code', cell: (r) => r.code ?? '—' },
        { head: 'Category', cell: (r) => r.category },
        { head: 'Optional', cell: (r) => (r.is_optional ? 'Yes' : 'No') },
        { head: 'Active', cell: (r) => (r.is_active ? 'Yes' : 'No') },
      ]}
    />
  );
}

function CreateStructure({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { sessions, terms } = useTerms();
  const heads = useGet<any>('feeheads', '/school/fee-heads');
  const { data: classes } = useClasses();
  const [f, setF] = useState({ name: '', academic_session_id: '', term_id: '', applies_to: 'class' });
  const [items, setItems] = useState([{ fee_head_id: '', amount: '', is_optional: false }]);
  const [classIds, setClassIds] = useState<number[]>([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const termOpts = terms.filter((t: any) => !f.academic_session_id || String(t.academic_session_id) === f.academic_session_id);

  async function submit() {
    setErr('');
    if (!f.name.trim() || !f.academic_session_id) return setErr('Name and session are required');
    const good = items.filter((i) => i.fee_head_id && Number(i.amount) > 0);
    if (good.length === 0) return setErr('Add at least one fee line with an amount');
    if (f.applies_to === 'class' && classIds.length === 0) return setErr('Choose at least one class');
    setBusy(true);
    try {
      await call('post', '/school/fee-structures', {
        body: {
          name: f.name,
          academic_session_id: Number(f.academic_session_id),
          term_id: f.term_id ? Number(f.term_id) : undefined,
          applies_to: f.applies_to,
          items: good.map((i) => ({ fee_head_id: Number(i.fee_head_id), amount: Number(i.amount), is_optional: i.is_optional })),
          class_ids: f.applies_to === 'class' ? classIds : undefined,
        },
      });
      onDone();
      onClose();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title="New fee structure" onClose={onClose} wide>
      <div className="space-y-3 text-sm">
        {err && <Notice kind="error">{err}</Notice>}
        <label className="block">Name<input className={inputCls} maxLength={120} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
        <div className="grid grid-cols-3 gap-3">
          <label>Session<select className={inputCls} value={f.academic_session_id} onChange={(e) => setF({ ...f, academic_session_id: e.target.value, term_id: '' })}><option value="">Select…</option>{sessions.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></label>
          <label>Term<select className={inputCls} value={f.term_id} onChange={(e) => setF({ ...f, term_id: e.target.value })}><option value="">Whole session</option>{termOpts.map((t: any) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></label>
          <label>Applies to<select className={inputCls} value={f.applies_to} onChange={(e) => setF({ ...f, applies_to: e.target.value })}><option value="class">Selected classes</option><option value="all">All students</option><option value="students">Specific students (set later)</option></select></label>
        </div>
        {f.applies_to === 'class' && <ClassChecks classes={arr(classes)} value={classIds} onChange={setClassIds} />}
        <p className="font-medium">Fee lines</p>
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center">
            <select className={`${inputCls} col-span-5`} value={it.fee_head_id} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, fee_head_id: e.target.value } : x)))}>
              <option value="">Fee head…</option>
              {arr(heads.data).filter((h: any) => h.is_active !== false).map((h: any) => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
            <input className={`${inputCls} col-span-3`} type="number" min={0.01} placeholder="Amount" value={it.amount} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))} />
            <label className="col-span-3 flex items-center gap-1"><input type="checkbox" checked={it.is_optional} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, is_optional: e.target.checked } : x)))} /> Optional</label>
            <button type="button" className="text-red-600 col-span-1" disabled={items.length === 1} onClick={() => setItems(items.filter((_, j) => j !== i))}>✕</button>
          </div>
        ))}
        <Button variant="ghost" onClick={() => setItems([...items, { fee_head_id: '', amount: '', is_optional: false }])}>+ Add line</Button>
        <div className="flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button disabled={busy} onClick={submit}>{busy ? 'Saving…' : 'Create draft'}</Button></div>
      </div>
    </Modal>
  );
}

function ClassChecks({ classes, value, onChange }: { classes: any[]; value: number[]; onChange: (v: number[]) => void }) {
  return (
    <div>
      <p className="font-medium mb-1">Classes</p>
      <div className="flex flex-wrap gap-3 max-h-32 overflow-y-auto">
        {classes.map((c) => (
          <label key={c.id} className="flex items-center gap-1">
            <input type="checkbox" checked={value.includes(c.id)} onChange={(e) => onChange(e.target.checked ? [...value, c.id] : value.filter((x) => x !== c.id))} /> {classLabel(c)}
          </label>
        ))}
        {classes.length === 0 && <span className="text-slate-500">No classes found.</span>}
      </div>
    </div>
  );
}

function StructureDetail({ id, onClose, onChanged }: { id: number; onClose: () => void; onChanged: () => void }) {
  const qc = useQueryClient();
  const q = useGet<any>('feestructure', `/school/fee-structures/${id}`);
  const heads = useGet<any>('feeheads', '/school/fee-heads');
  const { data: classes } = useClasses();
  const a = useAction(() => { qc.invalidateQueries({ queryKey: ['feestructure'] }); onChanged(); });
  const [dlg, setDlg] = useState<null | 'item' | 'targets'>(null);
  const [classIds, setClassIds] = useState<number[] | null>(null);
  const s = q.data;
  const draftish = s && s.status !== 'archived';
  return (
    <Modal title={s?.name ?? 'Fee structure'} onClose={onClose} wide>
      {q.isLoading && <p className="text-sm text-slate-500">Loading…</p>}
      {q.error && <Notice kind="error">{errMsg(q.error)}</Notice>}
      {a.banner}
      {s && (
        <div className="space-y-3">
          <KV k="Status" v={<StatusBadge status={s.status} />} />
          <KV k="Applies to" v={s.applies_to} />
          <KV k="Compulsory total" v={money(s.total)} />
          <KV k="Optional total" v={money(s.optional_total)} />
          <KV k="Invoices generated" v={s.invoices_count} />
          {s.students_to_bill != null && <KV k="Students still to bill" v={s.students_to_bill} />}
          <DataTable rows={s.items ?? []} empty="No fee lines yet." cols={[{ head: 'Fee', cell: (r: any) => r.name }, { head: 'Category', cell: (r: any) => r.category }, { head: 'Optional', cell: (r: any) => (r.is_optional ? 'Yes' : 'No') }, { head: 'Due', cell: (r: any) => dOnly(r.due_date) }, { head: 'Amount', right: true, cell: (r: any) => money(r.amount) }]} />
          <div className="flex gap-2 flex-wrap">
            {draftish && <Button variant="ghost" onClick={() => setDlg('item')}>Add / update fee line</Button>}
            {draftish && s.applies_to !== 'all' && <Button variant="ghost" onClick={() => { setClassIds(s.class_ids ?? []); setDlg('targets'); }}>Set classes</Button>}
            {s.status === 'draft' && <Button disabled={a.busy} onClick={() => a.run(() => call('post', `/school/fee-structures/${id}/activate`), { confirm: 'Activate this structure?' })}>Activate</Button>}
            {s.status === 'active' && <Button disabled={a.busy} onClick={() => a.run(() => call('post', `/school/fee-structures/${id}/generate-invoices`), { confirm: 'Generate invoices for every student in scope?' })}>Generate invoices</Button>}
            {s.status !== 'archived' && <Button variant="danger" disabled={a.busy} onClick={() => a.run(() => call('put', `/school/fee-structures/${id}`, { body: { status: 'archived' } }), { confirm: 'Archive this structure?' })}>Archive</Button>}
          </div>
        </div>
      )}
      {dlg === 'item' && (
        <FormModal
          title="Fee line"
          fields={[
            { name: 'fee_head_id', label: 'Fee head', type: 'select', required: true, options: arr(heads.data).map((h: any) => ({ value: h.id, label: h.name })) },
            { name: 'amount', label: 'Amount', type: 'number', required: true, min: 0.01 },
            { name: 'is_optional', label: 'Optional', type: 'checkbox' },
            { name: 'due_date', label: 'Due date', type: 'date' },
          ]}
          onClose={() => setDlg(null)}
          onSubmit={async (v) => { await call('post', `/school/fee-structures/${id}/items`, { body: { ...v, fee_head_id: Number(v.fee_head_id) } }); qc.invalidateQueries({ queryKey: ['feestructure'] }); onChanged(); }}
        />
      )}
      {dlg === 'targets' && classIds && (
        <Modal title="Target classes" onClose={() => setDlg(null)}>
          <ClassChecks classes={arr(classes)} value={classIds} onChange={setClassIds} />
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="ghost" onClick={() => setDlg(null)}>Cancel</Button>
            <Button onClick={() => { a.run(() => call('post', `/school/fee-structures/${id}/targets`, { body: { class_ids: classIds } }), { ok: 'Targets saved' }); setDlg(null); }}>Save</Button>
          </div>
        </Modal>
      )}
    </Modal>
  );
}

function Structures() {
  const qc = useQueryClient();
  const [open, setOpen] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ['feestructures'] });
  return (
    <>
      <ResourcePanel
        qkey="feestructures"
        url="/school/fee-structures"
        onRow={(r) => setOpen(r.id)}
        empty="No fee structures yet."
        extraAction={<Button onClick={() => setCreating(true)}>New structure</Button>}
        cols={[
          { head: 'Name', cell: (r) => r.name },
          { head: 'Applies to', cell: (r) => r.applies_to },
          { head: 'Total', right: true, cell: (r) => money(r.total) },
          { head: 'Invoices', right: true, cell: (r) => r.invoices_count },
          { head: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
        ]}
      />
      {open != null && <StructureDetail id={open} onClose={() => setOpen(null)} onChanged={refresh} />}
      {creating && <CreateStructure onClose={() => setCreating(false)} onDone={refresh} />}
    </>
  );
}

function Discounts() {
  const qc = useQueryClient();
  const q = useGet<any>('discounts', '/school/discounts');
  const heads = useGet<any>('feeheads', '/school/fee-heads');
  const { terms } = useTerms();
  const { data: classes } = useClasses();
  const refresh = () => qc.invalidateQueries({ queryKey: ['discounts'] });
  const a = useAction(refresh);
  const [form, setForm] = useState(false);
  const [applyTo, setApplyTo] = useState<any>(null);
  const list = arr(q.data?.discounts);
  return (
    <Card title="Discounts" action={<Button onClick={() => setForm(true)}>New discount</Button>}>
      {a.banner}
      <div className="flex items-center gap-2 mb-3 text-sm">
        <span className="text-slate-500">Sibling discount:</span>
        <select className="border rounded-xl px-3 py-2 bg-white" value={q.data?.sibling_discount_id ?? ''} onChange={(e) => a.run(() => putSiblingRule({ discount_id: e.target.value ? Number(e.target.value) : null }), { ok: 'Sibling rule saved' })}>
          <option value="">None</option>
          {list.filter((d: any) => d.approved).map((d: any) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>
      <DataTable
        rows={list}
        loading={q.isLoading}
        error={q.error}
        empty="No discounts defined."
        cols={[
          { head: 'Name', cell: (d: any) => d.name },
          { head: 'Value', cell: (d: any) => (d.type === 'percent' ? `${d.value}%` : money(d.value)) },
          { head: 'Scope', cell: (d: any) => d.scope },
          { head: 'Students', right: true, cell: (d: any) => d.students },
          { head: 'Status', cell: (d: any) => (d.approved ? 'Approved' : 'Awaiting owner approval') },
          {
            head: 'Actions',
            cell: (d: any) => (
              <span className="flex gap-3">
                {d.approved ? <Link2 onClick={() => setApplyTo(d)}>Apply to students</Link2> : <Link2 onClick={() => a.run(() => approveDiscount(d.id), { ok: 'Approved' })}>Approve</Link2>}
              </span>
            ),
          },
        ]}
      />
      {form && (
        <FormModal
          title="New discount"
          fields={[
            { name: 'name', label: 'Name', required: true, maxLength: 80 },
            { name: 'type', label: 'Type', type: 'select', required: true, options: [{ value: 'percent', label: 'Percent' }, { value: 'fixed', label: 'Fixed amount' }] },
            { name: 'value', label: 'Value', type: 'number', required: true, min: 0.01 },
            { name: 'scope', label: 'Scope', type: 'select', options: [{ value: 'invoice', label: 'Whole invoice' }, { value: 'fee_head', label: 'One fee head' }] },
            { name: 'fee_head_id', label: 'Fee head', type: 'select', options: arr(heads.data).map((h: any) => ({ value: h.id, label: h.name })), show: (v) => v.scope === 'fee_head', required: true },
            { name: 'reason', label: 'Reason', maxLength: 255 },
          ]}
          onClose={() => setForm(false)}
          onSubmit={async (v) => { await call('post', '/school/discounts', { body: v }); refresh(); }}
        />
      )}
      {applyTo && <ApplyDiscount discount={applyTo} terms={terms} classes={arr(classes)} onClose={() => setApplyTo(null)} onDone={refresh} />}
    </Card>
  );
}

function ApplyDiscount({ discount, terms, classes, onClose, onDone }: { discount: any; terms: any[]; classes: any[]; onClose: () => void; onDone: () => void }) {
  const [classId, setClassId] = useState('');
  const [termId, setTermId] = useState('');
  const students = useGet<any>('discstudents', '/school/students', { class_id: classId, per_page: 100 }, !!classId);
  const [picked, setPicked] = useState<number[]>([]);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const rows = arr(students.data);
  async function go() {
    setErr(''); setMsg('');
    try {
      const r = await call('post', '/school/discounts/apply', { body: { discount_id: discount.id, student_ids: picked, term_id: termId ? Number(termId) : undefined } });
      setMsg(r.message ?? 'Applied');
      onDone();
    } catch (e) {
      setErr(errMsg(e));
    }
  }
  return (
    <Modal title={`Apply "${discount.name}"`} onClose={onClose} wide>
      <div className="space-y-3 text-sm">
        {err && <Notice kind="error">{err}</Notice>}
        {msg && <Notice kind="ok">{msg}</Notice>}
        <div className="grid grid-cols-2 gap-3">
          <label>Class<select className={inputCls} value={classId} onChange={(e) => { setClassId(e.target.value); setPicked([]); }}><option value="">Select class…</option>{classes.map((c) => <option key={c.id} value={c.id}>{classLabel(c)}</option>)}</select></label>
          <label>Term<select className={inputCls} value={termId} onChange={(e) => setTermId(e.target.value)}><option value="">All terms</option>{terms.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}</select></label>
        </div>
        {students.isLoading && <p className="text-slate-500">Loading students…</p>}
        {classId && !students.isLoading && rows.length === 0 && <p className="text-slate-500">No students in this class.</p>}
        <div className="max-h-56 overflow-y-auto grid grid-cols-2 gap-1">
          {rows.map((s: any) => (
            <label key={s.id} className="flex items-center gap-2">
              <input type="checkbox" checked={picked.includes(s.id)} onChange={(e) => setPicked(e.target.checked ? [...picked, s.id] : picked.filter((x) => x !== s.id))} />
              {[s.first_name, s.last_name].filter(Boolean).join(' ') || s.name} <span className="text-slate-500">{s.admission_no}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-between items-center">
          {rows.length > 0 ? <Link2 onClick={() => setPicked(picked.length === rows.length ? [] : rows.map((s: any) => s.id))}>Toggle all</Link2> : <span />}
          <span className="flex gap-2"><Button variant="ghost" onClick={onClose}>Close</Button><Button disabled={picked.length === 0} onClick={go}>Apply to {picked.length}</Button></span>
        </div>
      </div>
    </Modal>
  );
}

/** Which fees a part-payment clears first. Empty order = the oldest invoice is cleared first. */
function PaymentOrder() {
  const qc = useQueryClient();
  const rule = useGet<any>('alloc-rule', '/school/fees/allocation-rules');
  const heads = useGet<any>('feeheads', '/school/fee-heads');
  const all = arr(heads.data);
  const [order, setOrder] = useState<number[] | null>(null);
  const [oldestTerm, setOldestTerm] = useState<boolean | null>(null);
  const a = useAction(() => qc.invalidateQueries({ queryKey: ['alloc-rule'] }));
  const cur: number[] = order ?? (rule.data?.order ?? []);
  const ot = oldestTerm ?? !!rule.data?.oldest_term_first;
  const name = (id: number) => all.find((h: any) => h.id === id)?.name ?? `Fee head ${id}`;
  const rest = all.filter((h: any) => !cur.includes(h.id));
  const move = (i: number, d: number) => { const n = [...cur]; const j = i + d; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; setOrder(n); };
  return (
    <Card>
      {a.banner}
      <p className="text-sm text-slate-600 mb-3">When a parent pays only part of what they owe, this decides which fees are cleared first. Leave the list empty to clear the oldest invoice first.</p>
      {rule.isLoading ? <p className="text-sm text-slate-500">Loading…</p> : (
        <>
          <ol className="space-y-1.5 mb-3">
            {cur.length === 0 && <li className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-xl px-3 py-3 text-center">Oldest invoice first (no order set)</li>}
            {cur.map((id, i) => (
              <li key={id} className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2 text-sm">
                <span className="w-6 h-6 rounded-full bg-[#0A2540] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <span className="flex-1 font-medium">{name(id)}</span>
                <button onClick={() => move(i, -1)} disabled={i === 0} className="px-2 text-slate-500 disabled:opacity-30" aria-label="Up">▲</button>
                <button onClick={() => move(i, 1)} disabled={i === cur.length - 1} className="px-2 text-slate-500 disabled:opacity-30" aria-label="Down">▼</button>
                <button onClick={() => setOrder(cur.filter((x) => x !== id))} className="px-2 text-red-600 text-xs">Remove</button>
              </li>
            ))}
          </ol>
          {rest.length > 0 && <div className="flex flex-wrap gap-1.5 mb-3">{rest.map((h: any) => <button key={h.id} onClick={() => setOrder([...cur, h.id])} className="px-2.5 py-1 rounded-full border border-slate-300 text-xs">+ {h.name}</button>)}</div>}
          <label className="flex items-center gap-2 text-sm mb-4"><input type="checkbox" checked={ot} onChange={(e) => setOldestTerm(e.target.checked)} />Clear older terms before the current term</label>
          <Button disabled={a.busy} onClick={() => a.run(() => call('put', '/school/fees/allocation-rules', { body: { order: cur, oldest_term_first: ot } }), { ok: 'Payment order saved.' })}>Save payment order</Button>
        </>
      )}
    </Card>
  );
}

export function FeeStructurePage() {
  const [tab, setTab] = useState('structures');
  useSessions();
  return (
    <div>
      <PageHeader title="Fee structure" sub="What each class pays, fee heads, discounts and which fees a part-payment clears first" />
      <Tabs value={tab} onChange={setTab} tabs={[{ id: 'structures', label: 'Structures' }, { id: 'heads', label: 'Fee heads' }, { id: 'discounts', label: 'Discounts' }, { id: 'order', label: 'Payment order' }]} />
      {tab === 'structures' && <Structures />}
      {tab === 'heads' && <FeeHeads />}
      {tab === 'discounts' && <Discounts />}
      {tab === 'order' && <PaymentOrder />}
    </div>
  );
}
